import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Rocket, PlayCircle, Gift, CheckCircle, Square, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { useOnboarding } from '../../context/OnboardingContext';

const WelcomeModal = ({ userRole, userName, userId }) => {
  // TEMPORARILY DISABLED - modal positioning issues
  // TODO: Re-enable once fixed
  return null;

  // Use user-specific localStorage key if userId provided, otherwise fallback to role-based
  const getStorageKey = () => userId ? `thm_welcome_hidden_${userId}` : `thm_welcome_seen_${userRole}`;
  const getDismissedKey = () => userId ? `thm_welcome_dismissed_${userId}` : `thm_welcome_dismissed_${userRole}`;

  useEffect(() => {
    // Check if user has permanently hidden or previously dismissed the welcome modal
    const hasHiddenWelcome = localStorage.getItem(getStorageKey());
    const hasDismissed = localStorage.getItem(getDismissedKey());
    const welcomeTourId = userRole === 'customer' ? 'customer_welcome' : 'provider_welcome';
    
    // Don't show if: hidden permanently, dismissed before, or tour completed
    if (!hasHiddenWelcome && !hasDismissed && !isTourCompleted(welcomeTourId)) {
      // Delay showing the modal for a better UX
      const timer = setTimeout(() => setIsOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [userRole, userId, isTourCompleted]);

  const handleStartTour = () => {
    // Always mark as dismissed when starting tour
    localStorage.setItem(getDismissedKey(), 'true');
    if (dontShowAgain) {
      localStorage.setItem(getStorageKey(), 'true');
    }
    setIsOpen(false);
    const tourId = userRole === 'customer' ? 'customer_welcome' : 'provider_welcome';
    setTimeout(() => startTour(tourId), 300);
  };

  const handleSkip = () => {
    // Always mark as dismissed when skipping
    localStorage.setItem(getDismissedKey(), 'true');
    if (dontShowAgain) {
      localStorage.setItem(getStorageKey(), 'true');
    }
    // Also mark the tour as completed so it doesn't show again
    const tourId = userRole === 'customer' ? 'customer_welcome' : 'provider_welcome';
    endTour(true); // Mark tour as complete
    setIsOpen(false);
  };

  const handleClose = () => {
    // X button always permanently hides the modal for this user
    localStorage.setItem(getStorageKey(), 'true');
    localStorage.setItem(getDismissedKey(), 'true');
    const tourId = userRole === 'customer' ? 'customer_welcome' : 'provider_welcome';
    endTour(true);
    setIsOpen(false);
  };

  const features = userRole === 'customer' ? [
    { icon: '1', text: 'Post jobs and get quotes from verified providers' },
    { icon: '2', text: 'Compare prices and select the best offer' },
    { icon: '3', text: 'Track your delivery in real-time' },
    { icon: '4', text: 'Secure payments with money-back guarantee' }
  ] : [
    { icon: '1', text: '14-day free trial - no charges until it ends' },
    { icon: '2', text: 'Get notified instantly when new jobs are posted' },
    { icon: '3', text: 'Submit competitive quotes and win more jobs' },
    { icon: '4', text: 'Get paid directly to your bank account' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[9995]"
            onClick={handleClose}
          />

          {/* Modal - centered container */}
          <div className="fixed inset-0 z-[9996] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md max-h-[90vh] flex flex-col"
            >
            {/* Close button - more prominent */}
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-20 shadow-md"
              aria-label="Close and don't show again"
              data-testid="welcome-close-btn"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            {/* Scrollable content wrapper */}
            <div className="overflow-y-auto flex-1">
              {/* Header with gradient */}
              <div className="bg-gradient-to-br from-orange-500 via-red-500 to-orange-600 px-6 py-8 text-white text-center relative overflow-hidden flex-shrink-0">
                {/* Background decoration */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>
                
                <div className="relative">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3"
                  >
                    <Rocket className="w-7 h-7" />
                  </motion.div>
                  <h2 className="text-xl font-bold mb-1">
                    Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}!
                  </h2>
                  <p className="text-white/80 text-sm">
                    {userRole === 'customer' 
                      ? "Let's get your freight moving" 
                      : "Ready to start winning jobs?"}
                  </p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                  <Gift className="w-4 h-4 text-orange-500" />
                  {userRole === 'customer' ? "Here's what you can do:" : "Your free trial includes:"}
                </h3>
                
                <div className="space-y-2 mb-6">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                      className="flex items-center gap-2"
                    >
                      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                      </div>
                      <p className="text-xs text-gray-600">{feature.text}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Actions */}
                <div className="space-y-3">
                  <Button
                    onClick={handleStartTour}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-5 text-sm gap-2"
                    data-testid="welcome-start-tour-btn"
                  >
                    <PlayCircle className="w-4 h-4" />
                    Take a Quick Tour (1 min)
                  </Button>
                  
                  <button
                    onClick={handleSkip}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 transition-colors py-2"
                    data-testid="welcome-skip-btn"
                  >
                    I'll explore on my own
                  </button>
                  
                  {/* Don't show again checkbox - more prominent */}
                  <button
                    onClick={() => setDontShowAgain(!dontShowAgain)}
                    className="w-full flex items-center justify-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors py-1 border-t border-gray-100 pt-3"
                    data-testid="dont-show-again-checkbox"
                  >
                    {dontShowAgain ? (
                      <CheckSquare className="w-4 h-4 text-orange-500" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    Don't show this welcome screen again
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WelcomeModal;
